// Artifact: a self-contained snapshot of a design at the moment of render.
//
// A block or a quilt is a *thing* — a finished design — and lives parallel
// to the running Project state. Every render_* tool call materializes one.
// Pins capture artifacts; opening a pin reseeds a new doc from one.
//
// FUTURE REFACTOR (deferred): today, a Project carries one current block +
// one current layout. A session may render multiple distinct artifacts as the
// user explores ideas — but the Project model only remembers the latest. A
// later refactor could promote artifacts to first-class history within a
// Project. The artifact format here is intentionally independent of that
// shape so it stays forward-compatible.
import type { Block, Fabric, Layout, Project } from "./core/state"
import { defaultLayout } from "./core/state"

export interface BlockArtifact {
  kind: "block"
  // Block geometry: schema, unit, seam, piece-role overrides.
  block: Block
  // Slot fabrics from the source colorway group, with names preserved
  // verbatim from the original fabric_add tool call. A pin re-opened
  // materializes its new project's fabrics from this list directly.
  fabrics: Fabric[]
  // Resolved slot → hex map used to render this artifact. Independent of
  // any preview render mode (color/value) — captures design intent only.
  colorway: Record<string, string>
}

export interface QuiltArtifact {
  kind: "quilt"
  projectName: string
  block: Block
  // Layout sans RNG state — dither_swaps/dither_seed are render-time noise.
  layout: Omit<Layout, "dither_swaps" | "dither_seed">
  // Every fabric in play at the moment of render: structural + all slot
  // fabrics across all colorway groups. Names preserved verbatim.
  fabrics: Fabric[]
  // Per-group slot → hex maps as configured at render time.
  colorways: Record<string, Record<string, string>>
}

export type Artifact = BlockArtifact | QuiltArtifact

// Build a block artifact from the project state at the moment a single block
// was rendered. `colorway` is the design colors (not value-greyscale). The
// fabric subset includes only slot fabrics referenced by this block in the
// source colorway group — the new project opened from a pin should have
// exactly the inputs needed, no stash leftovers.
export function makeBlockArtifact(
  project: Project,
  colorway: Record<string, string>,
  sourceGroup: string | undefined,
): BlockArtifact {
  const referencedSlots = new Set(Object.values(project.block.piece_roles))
  const fabrics = sourceGroup === undefined
    ? []
    : project.fabrics.filter(
        (f) => f.slot != null && referencedSlots.has(f.slot) && f.group === sourceGroup,
      )
  return {
    kind: "block",
    block: { ...project.block, piece_roles: { ...project.block.piece_roles } },
    fabrics: fabrics.map((f) => ({ ...f })),
    colorway: { ...colorway },
  }
}

// Build a quilt artifact from the full project state. Includes every fabric
// — quilts depend on structural fabrics (sashing, cornerstones, setting,
// binding, backing) plus the slot fabrics for every colorway group.
export function makeQuiltArtifact(project: Project): QuiltArtifact {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dither_swaps, dither_seed, ...layoutClean } = project.layout
  return {
    kind: "quilt",
    projectName: project.name,
    block: { ...project.block, piece_roles: { ...project.block.piece_roles } },
    layout: layoutClean,
    fabrics: project.fabrics.map((f) => ({ ...f })),
    colorways: Object.fromEntries(
      Object.entries(project.colorways).map(([g, c]) => [g, { ...c }]),
    ),
  }
}

// Materialize an artifact back into a Project for opening as a new doc.
// Block artifacts open into a block-only project (layout rows=cols=0); the
// caller marks the resulting ProjectDoc with kind="block". Quilt artifacts
// restore the full project shape with default RNG state.
export function projectFromArtifact(artifact: Artifact): Project {
  if (artifact.kind === "block") {
    return {
      name: "Untitled block",
      fabrics: artifact.fabrics.map((f) => ({ ...f })),
      block: { ...artifact.block, piece_roles: { ...artifact.block.piece_roles } },
      layout: defaultLayout(),
      colorways: { A: { ...artifact.colorway } },
    }
  }
  return {
    name: artifact.projectName,
    fabrics: artifact.fabrics.map((f) => ({ ...f })),
    block: { ...artifact.block, piece_roles: { ...artifact.block.piece_roles } },
    layout: { ...artifact.layout, dither_swaps: 0, dither_seed: 42 },
    colorways: Object.fromEntries(
      Object.entries(artifact.colorways).map(([g, c]) => [g, { ...c }]),
    ),
  }
}

// Cache shapes — pair the rendered SVG with the artifact that produced it
// so a tool-output cached on disk remains pinnable after a refresh. Every
// new tool call writes both fields; legacy entries from before the pin
// feature shipped are dropped on load (sanitizeToolOutputs) rather than
// migrated, so consumers can assume `artifact` is always present.
export interface RenderedBlock {
  svg: string
  artifact: BlockArtifact
}

export interface RenderedQuilt {
  svg: string
  artifact: QuiltArtifact
}

export interface RenderedThumbnail {
  svg: string
  label: string
  artifact: QuiltArtifact
}
